<template>
  <div class="p-6 mx-auto">
    <PageHeading
      class="mb-6"
      title="Events Documentation"
      description="Learn how to create, manage, and control events for your jukebox system."
      :crumbs="[{ label: 'Documentation' }, { label: 'Events' }]"
    />

    <div class="space-y-6">
      <!-- Overview Section -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Overview</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          The Events page allows you to create, edit, and manage voting events. Events define when
          voting is open, which songs are available, and control the live performance flow.
        </p>
      </section>

      <!-- Creating Events -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Creating Events</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">To create a new event:</p>
        <ol class="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Click the "Create New Event" button at the top of the Events page</li>
          <li>
            Choose an event type:
            <ul class="list-disc list-inside ml-6 mt-2 space-y-1">
              <li>
                <strong>Pre-voting:</strong> Open voting ahead of the show to plan the first set or
                full playlist. Scheduling only includes voting start and end times.
              </li>
              <li>
                <strong>Concert:</strong> Full live concert setup with event schedule, voting,
                setlists, and performance tracking.
              </li>
              <li><strong>Karaoke:</strong> Coming soon — not available for creation yet.</li>
            </ul>
          </li>
          <li>
            Fill in the required fields:
            <ul class="list-disc list-inside ml-6 mt-2 space-y-1">
              <li>
                <strong>Event Name:</strong> A descriptive name for your event - used as a title on
                the Events page and in the voting page
              </li>
              <li>
                <strong>Slug:</strong> URL-friendly identifier (required for QR code generation and
                for voting page URL). Only lowercase letters, numbers, and hyphens are allowed.
              </li>
              <li>
                <strong>Event Start/End Time:</strong> When the actual event takes place (Concert
                events only)
              </li>
              <li>
                <strong>Voting Start/End Time:</strong> When voting is open. For Pre-voting events,
                both voting start and end are required. For Concert events, voting end is optional.
              </li>
              <li>
                <strong>Discarded songs:</strong> (songs to exclude from voting) - enables you to
                exclude songs from voting for this specific event
              </li>
              <li>
                <strong>Theme selection:</strong> (light or dark) - defines which theme will be used
                for the voting page by default (users can switch theme on the voting page if they
                prefer). Good for day events where users might prefer light theme, and for night
                events where users might prefer dark theme.
              </li>
            </ul>
          </li>
          <li>
            Optionally configure:
            <ul class="list-disc list-inside ml-6 mt-2 space-y-1">
              <li>Location and organizer information - for your own reference</li>
              <li>Event description - for your own reference</li>
              <li>
                Private event - for your own reference whether it is a public concert or a private
                event
              </li>
              <li>Notes - for your own reference</li>
              <li>
                Disable event - enables you to disable the event from being visible to users; no
                voting page will be accessible
              </li>
              <li>
                Disable voting - enables you to disable voting for this specific event; voting page
                will be accessible, but no voting will be possible; related message will be
                displayed to the users that the event voting has been disabled
              </li>
            </ul>
          </li>
          <li>Click "Save Event" to create the event</li>
        </ol>
      </section>

      <!-- Song exclusions -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          Excluding songs from voting
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          In the event modal under <strong>Song Management</strong>, you can hide songs from the
          public voting list in two ways:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>
            <strong>Exclude categories</strong> — hides every song in those categories. If a song
            has multiple categories, it is hidden when any one of them is excluded.
          </li>
          <li>
            <strong>Exclude individual songs</strong> — one-off removals, including uncategorized
            songs not covered by a category rule.
          </li>
        </ul>
      </section>

      <!-- Pinned Songs -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Pinned Songs</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          Pinned songs stay at the top of the concert playlist in the order you choose, even when
          they have no votes. Use this for an opener the band always starts with. People can still
          vote for a pinned song; votes do not move it until you unpin it or it is performed.
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Open the event and go to Song Management</li>
          <li>Choose a song and click Add. The first song in the list is the opener</li>
          <li>Use the arrows to reorder, or the X to remove a pin</li>
          <li>Songs you exclude from the event cannot stay pinned</li>
        </ul>
      </section>

      <!-- Editing Events -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Editing Events</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">To edit an existing event:</p>
        <ol class="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Click the edit icon (pencil) next to the event in the table</li>
          <li>Modify any fields as needed</li>
          <li>Click "Save Event" to apply changes</li>
        </ol>
        <p class="text-gray-600 dark:text-gray-400">
          Note: Changes to active events may affect ongoing voting sessions.
        </p>
      </section>

      <!-- Event Status -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Event Status</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">Events can have different statuses:</p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li><strong>Upcoming:</strong> Voting has not started yet</li>
          <li><strong>Active:</strong> Voting is currently open</li>
          <li>
            <strong>Ended:</strong> Voting has closed; voting page will be accessible but with a
            sleeping cat animation
          </li>
          <li><strong>Disabled:</strong> Event is disabled and not accessible</li>
        </ul>
      </section>

      <!-- QR Code -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          QR Code Generation
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          Each event has a unique QR code that can be scanned to access the voting page:
        </p>
        <ol class="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Click the QR code icon in the Events page item</li>
          <li>
            The QR code modal will display the QR, 6-digit event code (to be used on "Enter Event
            Code" field on the voting page) and event URL
          </li>
          <li>
            You can right-click on the QR code, and use "Save As" link to download the QR code image
            for later use (sending it to the organizer or for printing)
          </li>
        </ol>
      </section>

      <!-- Live Event Control -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          Live Event Control
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          Once an event is active, you can access the Live Event control panel to:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>View real-time vote counts</li>
          <li>
            Add votes for songs (throgh the admin "Live Event" page multiple votes per song are
            allowed)
          </li>
          <li>Start playing songs (by vote count)</li>
          <li>
            Mark songs as performed / play next song; this will also end the current song if it is
            being played.
          </li>
          <li>Monitor the current song being played</li>
          <li>View performed songs history</li>
        </ul>
        <p class="text-gray-600 dark:text-gray-400">
          Access the Live Event page from the Live menu in the sidebar (visible while events are
          live) or from the active event banner on the Dashboard.
        </p>
      </section>
    </div>
  </div>
</template>

<script>
import PageHeading from "../../components/common/PageHeading.vue";

export default {
  name: "EventsDocumentation",
  components: { PageHeading }
};
</script>
